import React, { useState } from "react";
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

import FilterCard from "./FilterCard";

import ModalHeader from "@/Components/Modals/ModalHeader";
import {
  Age,
  UserAgeAttributeSet,
} from "@/schemas/profile/attributes/UserAgeAttributeSet";
import {
  Height,
  UserHeightAttributeSet,
} from "@/schemas/profile/attributes/UserHeightAttributeSet";
import {
  UserWeightAttributeSet,
  Weight,
} from "@/schemas/profile/attributes/UserWeightAttributeSet";
import {
  InitialFilterCriteria,
  type UserFilterCriteria,
} from "@/schemas/profile/filters/UserFilterCriteria";
import {
  type UserAttributeSet,
  UserAttributeType,
} from "@/schemas/profile/UserAttributeSet";
import { type FilterByRange } from "@/schemas/screen/filters/FilterByRange";

export interface FilterOption {
  rank: number;
  icon: string;
  label: string;
  attribute: UserAttributeSet;
}

interface FilterModalProps {
  isFilterModalVisible: boolean;
  onApplyFilters: (filters: UserFilterCriteria) => void;
  onClose: () => void;
}

function FilterModal({
  isFilterModalVisible,
  onApplyFilters,
  onClose,
}: FilterModalProps): React.JSX.Element {
  const [filters, setFilters] = useState<UserFilterCriteria>(
    InitialFilterCriteria,
  );

  const resetAllFilters = (): void => {
    setFilters(InitialFilterCriteria); // Reset to initial criteria
    onApplyFilters(InitialFilterCriteria); // Notify parent component
  };

  const filterOptions: FilterOption[] = [
    { rank: 1, icon: "🎂", label: "Age Range", attribute: UserAgeAttributeSet },
    {
      rank: 2,
      icon: "📏",
      label: "Height Range",
      attribute: UserHeightAttributeSet,
    },
    {
      rank: 3,
      icon: "⚖",
      label: "Weight Range",
      attribute: UserWeightAttributeSet,
    },
  ];

  const updateFilters = <T,>(
    newRange: FilterByRange<T>,
    type: UserAttributeType,
  ): void => {
    switch (type) {
      case UserAttributeType.HEIGHT:
        setFilters({ ...filters, height: newRange as FilterByRange<Height> });
        break;
      case UserAttributeType.WEIGHT:
        setFilters({ ...filters, weight: newRange as FilterByRange<Weight> });
        break;
      case UserAttributeType.AGE:
        setFilters({ ...filters, age: newRange as FilterByRange<Age> });
        break;
    }
  };

  return (
    <Modal visible={isFilterModalVisible} transparent>
      <Pressable style={styles.modalContainer} onPress={onClose}>
        <View
          style={styles.filterContainer}
          onStartShouldSetResponder={() => true}
        >
          <ModalHeader title="Filters" onClose={onClose} />
          <ScrollView>
            {filterOptions
              .sort((a, b) => a.rank - b.rank)
              .map((option) => {
                switch (option.attribute.id) {
                  case UserAttributeType.AGE:
                    return (
                      <FilterCard
                        key={option.attribute.id}
                        range={filters.age}
                        setFilters={updateFilters}
                        option={option}
                      />
                    );
                  case UserAttributeType.HEIGHT:
                    return (
                      <FilterCard
                        key={option.attribute.id}
                        range={filters.height}
                        setFilters={updateFilters}
                        option={option}
                      />
                    );
                  case UserAttributeType.WEIGHT:
                    return (
                      <FilterCard
                        key={option.attribute.id}
                        range={filters.weight}
                        setFilters={updateFilters}
                        option={option}
                      />
                    );
                  default:
                    return <></>;
                }
              })}
          </ScrollView>
          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={styles.resetAllButton}
              onPress={resetAllFilters}
            >
              <Text style={styles.resetAllButtonText}>Reset All</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.applyButton}
              onPress={() => onApplyFilters(filters)}
            >
              <Text style={styles.applyButtonText}>Apply Filters</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  filterContainer: {
    backgroundColor: "white",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    maxHeight: "80%",
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 20,
  },
  resetAllButton: {
    flex: 1,
    backgroundColor: "#F3F4F6",
    borderRadius: 10,
    padding: 15,
    marginRight: 10,
    alignItems: "center",
  },
  resetAllButtonText: {
    color: "#374151",
    fontWeight: "bold",
  },
  applyButton: {
    flex: 1,
    backgroundColor: "#F3F4F6",
    borderRadius: 10,
    padding: 15,
    marginLeft: 10,
    alignItems: "center",
  },
  applyButtonText: {
    color: "black",
    fontWeight: "bold",
  },
});

export default FilterModal;
